import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, MessageCircle, Share2, Copy, X, FileText, Mail, ExternalLink } from "lucide-react";

const BASE_PATH = (() => {
  const raw = process.env.PUBLIC_URL || "";
  try {
    return (new URL(raw).pathname || "").replace(/\/$/, "");
  } catch {
    return raw.replace(/\/$/, "");
  }
})();

const SITE_URL = (typeof window !== "undefined")
  ? `${window.location.origin}${BASE_PATH}`
  : `https://holidayicecream.in${BASE_PATH}`;
const PDF_URL = `${BASE_PATH}/assets/catalogue/Holiday-Ice-Creams-Catalogue-2026.pdf`;

const SHARE_TEXT = "Holiday Ice Creams - full digital catalogue\n80+ flavours | Open 24x7 | Bulk discounts on call.\nBrowse online: ";

export default function ShareCatalogue({ inline = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);

  useEffect(() => {
    setHasNativeShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);

  const shareUrl = SITE_URL;
  const fullText = SHARE_TEXT + shareUrl;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success("Catalogue link copied!");
    } catch {
      toast.error("Could not copy. Please copy the URL manually.");
    }
  };

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: "Holiday Ice Creams - Catalogue",
        text: SHARE_TEXT,
        url: shareUrl,
      });
    } catch {
      // User cancelled.
    }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent("Holiday Ice Creams - Digital Catalogue")}&body=${encodeURIComponent(fullText)}`;

  const Trigger = (
    <button
      onClick={() => setOpen(true)}
      data-testid="share-catalogue-trigger"
      className={
        inline
          ? `inline-flex items-center gap-2 bg-choc text-cream px-7 py-3.5 rounded-full font-bold hover:bg-choc/90 transition-colors ${className}`
          : "fixed z-30 bottom-28 md:bottom-6 right-4 md:right-6 inline-flex items-center gap-2 bg-holiday-red text-white pl-4 pr-5 py-3 rounded-full font-bold shadow-[0_12px_32px_-8px_rgba(230,57,70,0.6)] hover:bg-holiday-red-deep hover:scale-105 transition-all"
      }
      aria-label="Share digital catalogue"
    >
      <Share2 className="w-4 h-4" />
      <span className="text-sm">Share Catalogue</span>
    </button>
  );

  return (
    <>
      {Trigger}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="share-catalogue-modal">
          <div className="absolute inset-0 bg-choc/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-cream rounded-t-[2rem] sm:rounded-[2rem] cushion border border-[#F0E4E4] page-fade">
            <div className="relative p-7 md:p-9">
              <button onClick={() => setOpen(false)} className="absolute top-4 right-4 bg-white rounded-full p-2 shadow hover:bg-ice-pink" aria-label="Close" data-testid="share-modal-close">
                <X className="w-5 h-5" />
              </button>

              <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Digital Catalogue</p>
              <h2 className="font-display text-3xl md:text-4xl mt-2 leading-[1.05]">Share <span className="text-holiday-red">Holiday</span> with anyone.</h2>
              <p className="font-serif italic text-choc/70 mt-2">80+ flavours, contact info, and bulk pricing in one link.</p>

              <div className="mt-6 grid grid-cols-1 gap-3">
                <a
                  href={PDF_URL} target="_blank" rel="noreferrer" download="Holiday-Ice-Creams-Catalogue-2026.pdf"
                  data-testid="share-download-pdf"
                  className="flex items-center justify-between bg-holiday-red text-white px-5 py-4 rounded-2xl font-bold hover:bg-holiday-red-deep transition-colors"
                >
                  <span className="flex items-center gap-3"><FileText className="w-5 h-5"/> Get the full menu PDF</span>
                  <Download className="w-5 h-5" />
                </a>
                {hasNativeShare && (
                  <button
                    onClick={onNativeShare}
                    data-testid="share-native"
                    className="flex items-center justify-between bg-choc text-cream px-5 py-4 rounded-2xl font-bold hover:bg-choc/90 transition-colors"
                  >
                    <span className="flex items-center gap-3"><Share2 className="w-5 h-5"/> Share via apps...</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <a
                  href={waUrl} target="_blank" rel="noreferrer"
                  data-testid="share-whatsapp"
                  className="bg-mint hover:bg-mint-deep text-choc rounded-2xl py-4 flex flex-col items-center gap-1.5 transition-colors"
                >
                  <MessageCircle className="w-5 h-5"/>
                  <span className="text-[11px] font-bold uppercase tracking-widest">WhatsApp</span>
                </a>
                <a
                  href={mailUrl}
                  data-testid="share-email"
                  className="bg-ice-pink hover:bg-ice-pink-deep text-choc rounded-2xl py-4 flex flex-col items-center gap-1.5 transition-colors"
                >
                  <Mail className="w-5 h-5"/>
                  <span className="text-[11px] font-bold uppercase tracking-widest">Email</span>
                </a>
                <button
                  onClick={onCopy}
                  data-testid="share-copy"
                  className="bg-sun-yellow-soft hover:bg-sun-yellow text-choc rounded-2xl py-4 flex flex-col items-center gap-1.5 transition-colors"
                >
                  <Copy className="w-5 h-5"/>
                  <span className="text-[11px] font-bold uppercase tracking-widest">Copy link</span>
                </button>
              </div>

              <div className="mt-5 bg-white rounded-2xl p-4 border border-[#F0E4E4] flex items-center gap-3 text-xs">
                <span className="text-choc/60 font-mono truncate flex-1" data-testid="share-url-preview">{shareUrl}</span>
                <button onClick={onCopy} className="text-holiday-red font-bold hover:underline shrink-0">Copy</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
